import { useState } from 'react';
import type { SurveyAudienceMode, SurveyValidationIssue } from '@engage/types';
import { useValidateSurvey } from '../../../api/surveys.api';
import { Btn, Icon, ICONS } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';
import { hostedSurveyUrl } from '../hostedUrl';

/**
 * Publish modal. Fetches GET /surveys/:id/validate (the authoritative
 * flow-validator verdict, errors + warnings) and renders the readiness panel.
 * Errors block publish; warnings are advisory. On success it shows the hosted
 * link for anonymous surveys, or identity-link guidance for identified ones.
 */
export function PublishModal(props: {
  surveyId: string;
  slug: string;
  audienceMode: SurveyAudienceMode;
  nextVersionNumber: number;
  isPublishing: boolean;
  onClose: () => void;
  onPublish: () => void | Promise<void>;
}) {
  const validate = useValidateSurvey(props.surveyId, true);
  const [published, setPublished] = useState(false);

  const errors = validate.data?.errors ?? [];
  const warnings = validate.data?.warnings ?? [];
  const ready = !validate.isLoading && validate.isSuccess && errors.length === 0;

  const handlePublish = async () => {
    await props.onPublish();
    setPublished(true);
  };

  if (published) {
    return (
      <PublishSuccess
        slug={props.slug}
        audienceMode={props.audienceMode}
        versionNumber={props.nextVersionNumber}
        onClose={props.onClose}
      />
    );
  }

  return (
    <Shell onClose={props.onClose} width={500}>
      <Header
        title="Publish survey"
        subtitle={
          <>
            Publishing creates <b>version {props.nextVersionNumber}</b>. New responses use v
            {props.nextVersionNumber}; existing links keep working.
          </>
        }
        onClose={props.onClose}
      />

      <div style={{ padding: '0 24px 8px' }}>
        <Heading>READINESS</Heading>
        {validate.isLoading && <Muted>Checking the flow…</Muted>}
        {validate.isError && <Muted>Couldn’t validate: try again.</Muted>}

        {validate.isSuccess && errors.length === 0 && (
          <IssueRow tone="pass" message="Flow validated: no blocking issues" isLast={warnings.length === 0} />
        )}
        {errors.map((issue, i) => (
          <IssueRow key={`e-${i}`} tone="error" message={describe(issue)} isLast={false} />
        ))}
        {warnings.map((issue, i) => (
          <IssueRow key={`w-${i}`} tone="warn" message={describe(issue)} isLast={i === warnings.length - 1} />
        ))}
      </div>

      <div style={{ padding: '16px 24px 22px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <Btn kind="ghost" onClick={props.onClose}>
          Cancel
        </Btn>
        <Btn kind="primary" disabled={!ready || props.isPublishing} onClick={handlePublish}>
          {props.isPublishing ? 'Publishing…' : `Publish v${props.nextVersionNumber}`}
        </Btn>
      </div>

      {validate.isSuccess && errors.length > 0 && (
        <Banner tone="error">
          Resolve {errors.length} blocking {errors.length === 1 ? 'issue' : 'issues'} to enable publish
        </Banner>
      )}
      {ready && warnings.length > 0 && (
        <Banner tone="warn">
          {warnings.length} warning{warnings.length === 1 ? '' : 's'}. You can still publish.
        </Banner>
      )}
    </Shell>
  );
}

// ─── success view ───────────────────────────────────────────────────────────

function PublishSuccess(props: {
  slug: string;
  audienceMode: SurveyAudienceMode;
  versionNumber: number;
  onClose: () => void;
}) {
  const isAnon = props.audienceMode === 'ANONYMOUS';
  const hostedUrl = hostedSurveyUrl(props.slug);

  return (
    <Shell onClose={props.onClose} width={520}>
      <div style={{ padding: '26px 24px 8px', textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 46,
            height: 46,
            borderRadius: 99,
            background: C.contactBg,
            marginBottom: 12,
          }}
        >
          <Icon d={ICONS.check} size={22} color={C.tealDark} sw={2.6} />
        </span>
        <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>Survey published 🎉</h2>
        <div style={{ fontSize: 12.5, color: C.sub, marginTop: 4 }}>
          Version {props.versionNumber} is now live.
        </div>
      </div>

      <div style={{ padding: '16px 24px 6px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {isAnon ? (
          <CopyField label="Public survey link" value={hostedUrl} />
        ) : (
          <div
            style={{
              fontSize: 12.5,
              color: C.sub,
              lineHeight: 1.55,
              background: C.bg,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            This is an <b>identified</b> survey. Each recipient gets a unique tokenised link issued
            when you send it (campaign or direct send). Responses are tied to that contact/candidate
            and can be reminded automatically.
          </div>
        )}
      </div>

      <div style={{ padding: '12px 24px 22px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        {isAnon && (
          <Btn kind="ghost" onClick={() => window.open(hostedUrl, '_blank')}>
            <Icon d={ICONS.eye} size={15} color={C.ink} />
            Open survey
          </Btn>
        )}
        <Btn kind="primary" onClick={props.onClose}>
          Done
        </Btn>
      </div>
    </Shell>
  );
}

// ─── primitives ─────────────────────────────────────────────────────────────

function Shell(props: { onClose: () => void; width: number; children: React.ReactNode }) {
  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: props.width, background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,18,60,.28)', overflow: 'hidden' }}
      >
        {props.children}
      </div>
    </div>
  );
}

function Header(props: { title: string; subtitle: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{ padding: '22px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>{props.title}</h2>
        <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>{props.subtitle}</div>
      </div>
      <button
        type="button"
        onClick={props.onClose}
        aria-label="Close"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: C.faint }}
      >
        <Icon d={ICONS.x} size={20} color={C.faint} />
      </button>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, letterSpacing: 0.4, marginBottom: 10 }}>{children}</div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12.5, color: C.faint, padding: '8px 0' }}>{children}</div>;
}

function IssueRow(props: { tone: 'pass' | 'warn' | 'error'; message: string; isLast: boolean }) {
  const look = {
    pass: { bg: C.contactBg, fg: C.tealDark, icon: ICONS.check, text: C.ink },
    warn: { bg: C.warnBg, fg: C.warn, icon: ICONS.bolt, text: C.indigoSoft },
    error: { bg: C.dangerBg, fg: C.danger, icon: ICONS.x, text: C.danger },
  }[props.tone];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '10px 0',
        borderBottom: props.isLast ? 'none' : `1px solid ${C.line}`,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 99,
          flexShrink: 0,
          background: look.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon d={look.icon} size={12} color={look.fg} sw={2.6} />
      </span>
      <span style={{ fontSize: 13, color: look.text, fontWeight: props.tone === 'pass' ? 500 : 600, flex: 1 }}>
        {props.message}
      </span>
    </div>
  );
}

function Banner(props: { tone: 'warn' | 'error'; children: React.ReactNode }) {
  const look = props.tone === 'error' ? { bg: C.dangerBg, fg: C.danger } : { bg: C.warnBg, fg: C.warn };
  return (
    <div style={{ background: look.bg, color: look.fg, fontSize: 11.5, fontWeight: 600, textAlign: 'center', padding: '9px 0' }}>
      {props.children}
    </div>
  );
}

function CopyField(props: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — value still selectable */
    }
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, letterSpacing: 0.4, marginBottom: 6 }}>
        {props.label.toUpperCase()}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          readOnly
          value={props.value}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            color: C.ink,
            background: C.bg,
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            padding: '9px 11px',
          }}
        />
        <button
          type="button"
          onClick={copy}
          style={{
            flexShrink: 0,
            fontFamily: FORM_UI_FONT,
            fontSize: 12,
            fontWeight: 700,
            color: copied ? C.tealDark : C.indigo,
            background: copied ? C.contactBg : '#fff',
            border: `1px solid ${copied ? C.tealDark : C.line}`,
            borderRadius: 8,
            padding: '0 14px',
            cursor: 'pointer',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function describe(issue: SurveyValidationIssue): string {
  return issue.message;
}
